import React, { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSparkles } from "@fortawesome/pro-solid-svg-icons";
import {
    AiInsightCondition,
    BreedAiInsight,
    calculateSpotCost,
    formatCombinedVetBillRange,
    formatRate,
    formatVetBillRange,
    getAiInsightsByBreed,
    getPluralBreedLabel
} from "./aiInsightsData";
import { UIUtils } from "@/shared/utils/UIUtils";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import Strings from "@/shared/utils/Strings.constants";

interface PetInfo {
    name: string;
    breedName: string;
}

interface AiInsightsProps {
    pets: PetInfo[];
}

const LoadingDots: React.FC = () => {
    return (
        <div className="flex min-h-6 items-center gap-1">
            <div className="size-2 animate-[bounce-colored_1s_ease-in-out_0s_infinite] rounded-full" />
            <div className="size-2 animate-[bounce-colored_1s_ease-in-out_0.2s_infinite] rounded-full" />
            <div className="size-2 animate-[bounce-colored_1s_ease-in-out_0.4s_infinite] rounded-full" />
        </div>
    );
};

const ConditionFacts: React.FC<{
    condition: AiInsightCondition;
    petName: string;
    isMultiPet: boolean;
}> = ({ condition, petName, isMultiPet }) => {
    const spotCost = calculateSpotCost(condition.cost);
    const rateFormatted = formatRate(condition.propensity);

    return (
        <div className="flex flex-col gap-1 border-s-4 border-stroke-ai-info-light pb-[2px] ps-3">
            <p className="text-sm font-semibold text-content-ai-info">
                {isMultiPet && `${petName}'s `}
                {rateFormatted} {Strings.AI_INSIGHTS.MORE_PRONE_TO} {condition.title.toLowerCase()}
            </p>
            <p className="text-sm font-medium text-content-ai-info-light">
                {Strings.AI_INSIGHTS.AVG_VET_BILL} <span className="text-decoration-line font-bold line-through">{UIUtils.formatCurrency(condition.cost, { min: 0, max: 0 })}</span>
                <span className="font-semibold">
                    {" "}
                    ({UIUtils.formatCurrency(spotCost, { min: 0, max: 0 })} {Strings.AI_INSIGHTS.WITH_SPOT})
                </span>
            </p>
        </div>
    );
};

const AiInsights: React.FC<AiInsightsProps> = ({ pets }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [showContent, setShowContent] = useState(false);
    const [accordionValue, setAccordionValue] = useState<string | undefined>(undefined);

    // Move ALL useMemo hooks before any early returns
    const insightsData = useMemo(() => {
        const petsWithInsights = pets
            .map(pet => ({
                pet,
                insight: getAiInsightsByBreed(pet.breedName)
            }))
            .filter(item => item.insight !== undefined) as Array<{ pet: PetInfo; insight: BreedAiInsight }>;

        if (petsWithInsights.length === 0) return null;
        return petsWithInsights;
    }, [pets]);

    const isMultiPet = (insightsData?.length ?? 0) > 1;

    const combinedVetBillRange = useMemo(() => {
        if (!insightsData || insightsData.length === 0) return "";
        return isMultiPet ? formatCombinedVetBillRange(insightsData.map(item => item.insight.vetBillRange)) : formatVetBillRange(insightsData[0]!.insight.vetBillRange);
    }, [insightsData, isMultiPet]);

    const displayInsights = useMemo(() => {
        if (!insightsData || insightsData.length === 0) return [];

        if (!isMultiPet) {
            return [
                {
                    petName: insightsData[0]!.pet.name,
                    conditions: insightsData[0]!.insight.conditions.slice(0, 3)
                }
            ];
        }

        const usedConditionTitles = new Set<string>();
        return insightsData.map(({ pet, insight }) => {
            const availableCondition = insight.conditions.find(condition => !usedConditionTitles.has(condition.title));

            if (availableCondition) {
                usedConditionTitles.add(availableCondition.title);
                return {
                    petName: pet.name,
                    conditions: [availableCondition]
                };
            }

            // Fallback: use first condition (we know it exists since breeds have conditions)
            return {
                petName: pet.name,
                conditions: [insight.conditions[0]!]
            };
        });
    }, [insightsData, isMultiPet]);

    // Early return AFTER all hooks
    if (!insightsData) return null;

    const handleValueChange = async (value: string) => {
        if (value === "item-1") {
            // Opening
            setAccordionValue(value);
            setIsLoading(true);

            // 3 second delay to simulate LLM thinking
            setTimeout(() => {
                setIsLoading(false);
                setShowContent(true);
            }, 3000);
        } else {
            // Closing
            setAccordionValue(value);
            setShowContent(false);
        }
    };

    const isCollapsed = accordionValue !== "item-1";
    const contentId = "ai-insights-content";

    const handleBannerClick = () => {
        // Only allow clicking when collapsed
        if (accordionValue !== "item-1") {
            handleValueChange("item-1");
        }
    };

    const handleBannerKeyDown = (e: React.KeyboardEvent) => {
        if (isCollapsed && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            handleValueChange("item-1");
        }
    };

    return (
        <Accordion
            type="single"
            collapsible
            className="rounded-xl bg-background-ai-info px-3 pb-1"
            value={accordionValue}
            onValueChange={handleValueChange}
            data-testid="ai-insights-banner"
        >
            <AccordionItem value="item-1" className="border-0">
                <div
                    className={isCollapsed ? "cursor-pointer rounded-lg focus-visible:shadow-[0_0_0_4px_rgba(0,124,131,0.4)] focus-visible:outline-none" : ""}
                    onClick={isCollapsed ? handleBannerClick : undefined}
                    onKeyDown={isCollapsed ? handleBannerKeyDown : undefined}
                    role={isCollapsed ? "button" : undefined}
                    tabIndex={isCollapsed ? 0 : undefined}
                    aria-expanded={!isCollapsed}
                    aria-controls={isCollapsed ? contentId : undefined}
                >
                    {/* Top banner text */}
                    <div className="px-1 py-2 text-sm font-medium text-content-ai-info">
                        {isMultiPet ? (
                            <>
                                Your pets&apos; breeds can hit <span className="font-bold">{combinedVetBillRange}</span> {Strings.AI_INSIGHTS.VET_BILL_TEXT}
                            </>
                        ) : (
                            <>
                                {getPluralBreedLabel(insightsData[0]!.insight.breedName)} can hit <span className="font-bold">{combinedVetBillRange}</span>{" "}
                                {Strings.AI_INSIGHTS.VET_BILL_TEXT}
                            </>
                        )}
                    </div>

                    {showContent && (
                        <AccordionContent id={contentId} className="mt-1 flex flex-col gap-3 pb-2 text-content-secondary">
                            {displayInsights.map(({ petName, conditions }) => (
                                <React.Fragment key={petName}>
                                    {conditions
                                        .filter((c): c is AiInsightCondition => c !== undefined)
                                        .map(condition => (
                                            <ConditionFacts key={`${petName}-${condition.title}`} condition={condition} petName={petName} isMultiPet={isMultiPet} />
                                        ))}
                                </React.Fragment>
                            ))}
                        </AccordionContent>
                    )}

                    {/* Footer */}
                    <div className="flex items-center justify-between text-xs text-content-ai-info-light">
                        <span className="flex items-center gap-1 font-bold">
                            {Strings.AI_INSIGHTS.FOOTER_LABEL} <FontAwesomeIcon icon={faSparkles} />
                        </span>
                        {isLoading ? (
                            <LoadingDots />
                        ) : (
                            <AccordionTrigger className={`items-center gap-1 py-1 text-xs font-medium text-content-ai-info-light ${isCollapsed ? "pointer-events-none" : ""}`}>
                                {accordionValue === "item-1" ? Strings.AI_INSIGHTS.CLOSE : Strings.AI_INSIGHTS.SEE_MORE}
                            </AccordionTrigger>
                        )}
                    </div>
                </div>
            </AccordionItem>
        </Accordion>
    );
};

export default AiInsights;
